import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import type { MeasuredTransformerLoading } from '../../types/amiAnalytics.ts'
import { MEASURED_STATUS_COLORS } from '../../types/amiAnalytics.ts'
import type { LoadingBasis } from '../../types/engineering.ts'
import { Term } from '../common/Term.tsx'
import { fmtPct } from '../../utils/format.ts'

interface Props {
  transformers: MeasuredTransformerLoading[]
  /** The avg_demand_kw request parameter used for the estimated column and the
   *  hybrid silent-meter adder — the header and chip tooltips derive from it
   *  (B4). */
  avgDemandKw: number
}

type SortKey =
  | 'name'
  | 'kva_rating'
  | 'max_loading_pct'
  | 'effective_loading_pct'
  | 'p95_loading_pct'
  | 'estimated_loading_pct'
  | 'days_over_100'
  | 'window_coverage_pct'

/** Per-row basis chip (mirrors TransformerLoadingTable): every row is labeled
 *  measured / hybrid / model so the effective-based Status chip never reads as
 *  an unexplained mismatch against the pure-measured max %. */
function BasisChip({ basis, avgDemandKw }: { basis: LoadingBasis; avgDemandKw: number }) {
  if (basis === 'hybrid') {
    return (
      <span
        className="inline-block mr-1 px-1 py-0.5 text-[10px] leading-none rounded font-normal align-middle"
        style={{ backgroundColor: '#fffbeb', color: '#b45309' }}
        title={`Hybrid: measured coincident peak + a flat ${avgDemandKw} kW estimate added for each silent, non-reporting meter — deliberately conservative`}
      >
        meas+est
      </span>
    )
  }
  if (basis === 'measured') {
    return (
      <span
        className="inline-block mr-1 px-1 py-0.5 text-[10px] leading-none rounded font-normal align-middle"
        style={{ backgroundColor: '#f5f3ff', color: '#7c3aed' }}
        title="AMI measured peak over the window (every meter reporting)"
      >
        meas.
      </span>
    )
  }
  return (
    <span
      className="inline-block mr-1 px-1 py-0.5 text-[10px] leading-none rounded bg-gray-100 text-gray-500 font-normal align-middle"
      title="Below the coverage/reporting gate — insufficient measured data (status shows insufficient data)"
    >
      est.
    </span>
  )
}

export function MeasuredLoadingTable({ transformers, avgDemandKw }: Props) {
  // Default to the EFFECTIVE loading so an effectively-overloaded hybrid bank
  // (low pure-measured max %, high effective %) sorts to the top for triage
  // instead of hiding behind its measured max. Model-basis rows have no
  // effective value and sort to the bottom.
  const [sortKey, setSortKey] = useState<SortKey>('effective_loading_pct')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [statusFilter, setStatusFilter] = useState('')

  const rows = useMemo(() => {
    let items = transformers
    if (statusFilter) items = items.filter(t => t.loading_status === statusFilter)
    return [...items].sort((a, b) => {
      const va = (a[sortKey] ?? -1) as number | string
      const vb = (b[sortKey] ?? -1) as number | string
      if (va < vb) return sortDir === 'asc' ? -1 : 1
      if (va > vb) return sortDir === 'asc' ? 1 : -1
      return 0
    })
  }, [transformers, sortKey, sortDir, statusFilter])

  const header = (label: ReactNode, key: SortKey) => (
    <th
      scope="col"
      aria-sort={sortKey === key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 cursor-pointer select-none"
      onClick={() => {
        if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
        else { setSortKey(key); setSortDir('desc') }
      }}
    >
      {label}{sortKey === key ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
    </th>
  )

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-700">
          Measured Loading ({rows.length})
        </h3>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="text-xs border border-gray-300 rounded px-2 py-1"
        >
          <option value="">All Statuses</option>
          <option value="overloaded">Overloaded</option>
          <option value="heavy">Heavy</option>
          <option value="normal">Normal</option>
          <option value="underloaded">Underloaded</option>
          <option value="insufficient_data">Insufficient data</option>
          <option value="no_rating">No Rating</option>
        </select>
      </div>
      {/* The Status chip is the effective (measured + est. per silent meter)
          classification; Measured max % stays PURE measured. The basis chip and
          the Effective % column explain any gap between the two. */}
      <div className="overflow-x-auto max-h-96 overflow-y-auto">
        <table className="min-w-full text-xs">
          <thead className="sticky top-0 bg-white">
            <tr>
              {header('Transformer', 'name')}
              {header(<Term k="kva">kVA</Term>, 'kva_rating')}
              {header(<Term k="coincident">Measured max %</Term>, 'max_loading_pct')}
              {header('Effective %', 'effective_loading_pct')}
              {header('p95 %', 'p95_loading_pct')}
              {header(`Est. % (${avgDemandKw} kW/meter)`, 'estimated_loading_pct')}
              {header('Days >100%', 'days_over_100')}
              {header('Coverage %', 'window_coverage_pct')}
              <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500">
                Reporting
              </th>
              <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map(t => (
              <tr key={t.transformer_id} className="hover:bg-gray-50">
                <td className="px-2 py-1.5">
                  <BasisChip basis={t.loading_basis} avgDemandKw={avgDemandKw} />
                  {t.name ?? t.gis_id ?? t.transformer_id}
                </td>
                <td className="px-2 py-1.5">{t.kva_rating ?? '—'}</td>
                <td
                  className="px-2 py-1.5 font-medium"
                  title={t.max_loading_pct != null ? `${t.max_loading_pct}%` : undefined}
                >
                  {fmtPct(t.max_loading_pct)}
                </td>
                <td
                  className="px-2 py-1.5 font-medium"
                  style={{
                    color: t.loading_basis === 'hybrid'
                      ? MEASURED_STATUS_COLORS[t.loading_status] ?? undefined
                      : undefined,
                  }}
                  title={t.effective_loading_pct != null ? `${t.effective_loading_pct}%` : undefined}
                >
                  {fmtPct(t.effective_loading_pct)}
                </td>
                <td
                  className="px-2 py-1.5"
                  title={t.p95_loading_pct != null ? `${t.p95_loading_pct}%` : undefined}
                >
                  {fmtPct(t.p95_loading_pct)}
                </td>
                <td
                  className="px-2 py-1.5 text-gray-500"
                  title={t.estimated_loading_pct != null ? `${t.estimated_loading_pct}%` : undefined}
                >
                  {fmtPct(t.estimated_loading_pct)}
                </td>
                <td className="px-2 py-1.5">{t.days_over_100 || '—'}</td>
                <td
                  className="px-2 py-1.5"
                  title={t.window_coverage_pct != null ? `${t.window_coverage_pct}%` : undefined}
                >
                  {fmtPct(t.window_coverage_pct)}
                </td>
                <td className="px-2 py-1.5">{t.meters_reporting}/{t.meter_count}</td>
                <td className="px-2 py-1.5">
                  <span
                    className="inline-block px-1.5 py-0.5 rounded text-white text-[10px]"
                    style={{
                      backgroundColor: MEASURED_STATUS_COLORS[t.loading_status] ?? '#9ca3af',
                    }}
                  >
                    {t.loading_status.replace(/_/g, ' ')}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
